import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box, Dialog, DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  DialogTitle,
  FormControl,
  Stack,
  Divider
} from '@mui/material';

import { Close, EditOutlined,KeyboardArrowDownOutlined } from '@mui/icons-material';

import {
  defaultProviderResponse,
  providerResponseFallback,
  recommendationOptions
} from './ProviderResponseModalData';

import {
  editIconSx, issueHeaderSx,
  issueTextSx,
  metaTextSx,
  patientBoxSx,
  patientMrSx,
  patientNameSx,
  recommendationSelectSx, sectionTitleSx
} from '../../Common/components/Styles/CommunicationDialogStyles';

import {
  menuProps,
  textFieldSx,
  multitextFieldSx,
  dialogHeaderStyles
} from '../../Common/components/Styles/InputStyles';
import CommonDialogActions from '../../Common/components/CommonDialogActions';

const ProviderResponseModal = ({ open, onClose, onSubmit, data }) => {
  const [providerResponse, setProviderResponse] = useState(
    defaultProviderResponse
  );

  const [recommendation, setRecommendation] = useState('Medication');

  useEffect(() => {
    if (!open) {
      setProviderResponse(defaultProviderResponse);
      setRecommendation('Medication');
    }
  }, [open]);

  const patientData = {
    ...providerResponseFallback,
    ...data
  };

  const isValid = Boolean(providerResponse.trim() && recommendation);

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    onSubmit?.({
      providerResponse: providerResponse.trim(),
      recommendation,
      patient: patientData
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px'
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            Provider Response
          </Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
            {' '}
            Review and respond to staff communications
          </Typography>
        </Box>

        <IconButton
          aria-label="Close create issue dialog"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <Close sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, my: 2 }}>
        <Box sx={patientBoxSx}>
          <Typography sx={patientNameSx}>{patientData.patient}</Typography>

          <Typography sx={patientMrSx}>MR# {patientData.mrNumber}</Typography>
        </Box>

        <Box sx={issueHeaderSx}>
          <Typography sx={sectionTitleSx}>Issue Note</Typography>

          <EditOutlined sx={editIconSx} />
        </Box>

        <Typography sx={metaTextSx}>
          Category: {patientData.category || '-'}
          {' \u2022 '}
          Priority: {patientData.priority || '-'}
        </Typography>

        <Stack spacing={2}>
          <Typography sx={issueTextSx}>{patientData.issueNote}</Typography>
          <Divider />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Provider Response"
            value={providerResponse}
            onChange={event => setProviderResponse(event.target.value)}
            sx={multitextFieldSx}
          />
          <Divider />
          <FormControl fullWidth required size="small" sx={textFieldSx}>
            <Select
              fullWidth
              value={recommendation}
              IconComponent={KeyboardArrowDownOutlined}
              onChange={event => setRecommendation(event.target.value)}
              MenuProps={menuProps}
              sx={recommendationSelectSx}>
              {recommendationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <CommonDialogActions
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitText="Submit Response"
        disabled={!isValid}
      />
    </Dialog>
  );
};

ProviderResponseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  data: PropTypes.shape({
    patient: PropTypes.string,
    mrNumber: PropTypes.string,
    category: PropTypes.string,
    priority: PropTypes.string,
    issueNote: PropTypes.string
  })
};

ProviderResponseModal.defaultProps = {
  data: null,
  onSubmit: undefined
};

export default ProviderResponseModal;
